/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useCallback, useMemo } from 'react';
import cn from 'classnames';
import {
  func,
  shape,
  string,
  number,
  arrayOf,
  objectOf,
  bool,
  oneOfType,
} from 'prop-types';
import {
  Icon, Checkbox, Button, IconButton,
} from '@picsio/ui';
import {
  Notification,
  Close,
  Retry,
  AddFileImport,
  ReplaceFileImport,
  AddRevisionImport,
  FolderIcon,
  Ok,
  OpenIn,
  DropboxFolder,
} from '@picsio/icons';
import { useSelector } from 'react-redux';
import ua from '../../ua';
import Logger from '../../services/Logger';
import l10n from '../../shared/strings';
import Tooltip from '../Tooltip';

import './styles.scss';

const noop = Function.prototype;

const UploadItem = ({
  item,
  className = null,
  remove,
  retry = () => {},
  top = 0,
  groups,
  isSelected = false,
  toggleSelection = () => {},
  showError = false,
  isUploadStarted = false,
}) => {
  const isFolderFromCloud = item?.file?.['.tag'] === 'folder';
  const isMobile = useMemo(() => ua.browser.isNotDesktop(), []);
  const user = useSelector((state) => state.user);
  const teammateRoleId = useMemo(() => user.parent && user.parent.teammateRoleId, [user.parent]);
  const isOwner = useMemo(() => !teammateRoleId, [teammateRoleId]);
  const fileIcon = useMemo(() => {
    switch (item.action) {
    case 'addRevision':
      return <AddRevisionImport />;
    case 'replaceFile':
      return <ReplaceFileImport />;
    default:
      return (
        <Choose>
          <When condition={isFolderFromCloud}>
            <DropboxFolder />
          </When>
          <Otherwise>
            <AddFileImport />
          </Otherwise>
        </Choose>
      );
    }
  }, [item.action, isFolderFromCloud]);

  const handleRetry = useCallback(
    (event) => {
      event.stopPropagation();
      retry(item.id);
    },
    [retry, item.id],
  );

  const handleRemove = useCallback(
    (event) => {
      event.stopPropagation();
      remove([item.id]);
    },
    [remove, item.id],
  );
  const handleRemoveGroup = useCallback(
    (event) => {
      event.stopPropagation();
      remove(groups[item.groupPath].map(({ id }) => id));
    },
    [groups, item.groupPath, remove],
  );

  const handleClickSource = useCallback(() => {
    Logger.log('User', 'SourceUploadItemClicked');
    window.open(item.source, '_blank');
  }, [item.source]);

  const toggleSelectGroup = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (isUploadStarted) return;

      toggleSelection(item.groupPath);
    },
    [item, toggleSelection, isUploadStarted],
  );

  const toggleSelectFile = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (isUploadStarted) return;

      toggleSelection(item.id, event.shiftKey, event.altKey);
    },
    [item, toggleSelection, isUploadStarted],
  );

  const errorMessage = useMemo(() => {
    if (!item.isValid) {
      /** handle validation error, titleAndDescription and assetMarks has separated fields */
      const fields = Object.keys(item.errors || {}).filter(
        (key) => !(key === 'assetMarks' || key === 'titleAndDescription'),
      );
      return l10n.IMPORT.ITEM.validationFieldsError(fields);
    }
    return item.error?.message;
  }, [item]);

  if (item.groupPath) {
    /** Render collection */
    return (
      <div
        className="importFileItem collectionPath"
        style={{ top }}
        data-testid={`importRowCollection-${item.id}`}
      >
        <div className="importFileItemHolder" onClick={toggleSelectGroup}>
          <div className="importCell importName">
            <Checkbox
              checked={isSelected || isUploadStarted}
              onChange={noop}
              disabled={isUploadStarted}
              inputProps={{
                'data-testid': `importCheckboxCollection-${item.id}`,
              }}
            />
            <span className="iconHolder">
              <Icon size="sm">
                <FolderIcon />
              </Icon>
            </span>
            <span className="importItemCurrentName">{item.name}</span>
          </div>
          <Choose>
            <When condition={isMobile}>
              <IconButton onClick={handleRemoveGroup}>
                <Close />
              </IconButton>
            </When>
            <Otherwise>
              <Button
                onClick={handleRemoveGroup}
                onKeyPress={handleRemoveGroup}
                tabIndex={0}
                endIcon={<Close />}
                componentProps={{ 'data-testid': 'importRemoveCollection' }}
              >
                {l10n.IMPORT.ITEM.removeAll}
              </Button>
            </Otherwise>
          </Choose>
        </div>
      </div>
    );
  }

  /** Render file */
  return (
    <div
      className={cn('importFileItem', {
        error: !!item.error && showError,
        isInProgress: !!item.progress,
        [className]: className,
        isComplete: item.complete,
      })}
      style={{ top }}
    >
      <div
        className="importFileItemHolder"
        onClick={
          !item?.file?.['.tag'] ? toggleSelectFile : () => {}
        }
        data-testid={`importFileRow-${item.name}`}
      >
        <Tooltip content={item?.file?.['.tag'] ? 'Adding metadata to Dropbox-imported files/folders isn\’t possible on the import screen. This will be available once the folder and its contents are imported.' : null} placement="top">
          <div
            className={cn('importItemCell', {
              disabled: item?.file?.['.tag'],
            })}
          >
            <Checkbox
              checked={isSelected || isUploadStarted}
              onChange={noop}
              disabled={isUploadStarted}
              inputProps={{ 'data-testid': `importCheckboxFile-${item.name}` }}
            />
            <If condition={item.complete}>
              <span className="btnImportItemAction statusUploadComlete">
                <Icon size="sm">
                  <Ok />
                </Icon>
              </span>
            </If>
            {item.error && showError && item.isValid && (
              <Tooltip content={l10n.IMPORT.textRetry} placement="top">
                <span
                  className="btnImportItemAction"
                  onClick={handleRetry}
                  onKeyPress={handleRetry}
                  tabIndex={0}
                  role="button"
                >
                  <Icon size="sm">
                    <Retry />
                  </Icon>
                </span>
              </Tooltip>
            )}
            <If condition={!showError || !item.isValid}>
              <Choose>
                <When condition={item.source}>
                  <Tooltip content={item.source}>
                    <span
                      className="iconHolder clickable"
                      role="button"
                      tabIndex={0}
                      onKeyDown={handleClickSource}
                      onClick={handleClickSource}
                    >
                      <Icon size="sm">
                        <OpenIn />
                      </Icon>
                    </span>
                  </Tooltip>
                </When>
                <Otherwise>
                  <span className="iconHolder">
                    <Icon size={isFolderFromCloud ? 'md' : 'sm'}>
                      {fileIcon}
                    </Icon>
                  </span>
                </Otherwise>
              </Choose>
            </If>
            <span className="importItemCurrentName">{item.name}</span>
          </div>
        </Tooltip>
        <div className="importItemSize">
          <If condition={!isFolderFromCloud}>
            {item.shortSize}
          </If>
          <If condition={!item.complete}>
            <Tooltip content={l10n.IMPORT.textCancel} placement="top">
              {/* Disabled if file uploaded(or almost uploaded) but not saved on the server */}
              <IconButton
                onClick={handleRemove}
                disabled={(item.progress || 0) > 98}
                componentProps={{ 'data-testid': 'importRemoveFile' }}
              >
                <Close />
              </IconButton>
            </Tooltip>
          </If>
        </div>
        <If condition={item.progress}>
          <div
            className="importFileItemProgress"
            style={{ width: `${item.progress}%` }}
          />
        </If>
      </div>
      <If condition={item.error && showError}>
        <div className="importItemError">
          <span className="iconHolder">
            <Icon>
              <Notification />
            </Icon>
          </span>
          <Tooltip content={item.error?.message} placement="top">
            <span className="importItemCurrentName">{errorMessage}</span>
          </Tooltip>
        </div>
      </If>
      <If condition={item.file?.importFrom && !isOwner}>
        <div className="importItemWarning">
          <span className="iconHolder">
            <Icon>
              <Notification />
            </Icon>
          </span>
          <Choose>
            <When condition={item.file['.tag'] === 'file'}>
              <Tooltip content={l10n.IMPORT.textRequiredFieldWarning} placement="top">
                <span className="importItemCurrentName">{l10n.IMPORT.textImportingRequiredFieldFileWarning}</span>
              </Tooltip>
            </When>
            <Otherwise>
              <Tooltip content={l10n.IMPORT.textRequiredFieldWarning} placement="top">
                <span className="importItemCurrentName">{l10n.IMPORT.textImportingRequiredFieldFolderWarning}</span>
              </Tooltip>
            </Otherwise>
          </Choose>
        </div>
      </If>
    </div>
  );
};

export const itemShape = {
  progress: number,
  name: string,
  error: shape({ message: string }),
  file: shape({ name: string }),
  id: oneOfType([string, number]),
  groupPath: string,
  complete: bool,
  source: string,
};

UploadItem.propTypes = {
  item: shape(itemShape).isRequired,
  groups: objectOf(arrayOf(shape(itemShape))).isRequired,
  remove: func.isRequired,
  retry: func,
  isSelected: bool,
  toggleSelection: func,
  className: string,
  top: number,
  showError: bool,
  isUploadStarted: bool,
};

export default UploadItem;
